// @ts-check
// /api/academy/people — PeopleEndpoints.
//
// THE REFERENCE SERVICE. Every shape the API uses appears here once:
// cursor-paged list, Opt<T> patch, batch patch with a required mode,
// append-only sub-resources, and a write behind a different policy from its
// own controller's. (The idempotent-create example moved with its screen —
// cohortsService.createCohort shows the withIdempotency pattern now.) New
// service modules should read like this one.

import { get, post, patch, isEmptyPatch } from '../../api';

/**
 * @typedef {import('@academy-api').PersonResponse} PersonResponse
 * @typedef {import('@academy-api').PersonListItemResponse} PersonListItemResponse
 * @typedef {import('@academy-api').PersonPatchRequest} PersonPatchRequest
 * @typedef {import('@academy-api').LinkIdentityRequest} LinkIdentityRequest
 * @typedef {import('@academy-api').PageRequest} PageRequest
 * @typedef {import('@academy-api').BatchMode} BatchMode
 */

/**
 * One person, with the derived standing block for instructors.
 * @param {string} id
 * @returns {Promise<PersonResponse>}
 */
export const getPerson = (id, options = {}) => get(`/people/${id}`, options);

/**
 * The list. Returns PersonListItemResponse — deliberately thinner than
 * PersonResponse and NOT the entity: no standing block, and no credentials
 * collection. Use getPerson for the full record.
 *
 * `options.kind` is one of `learner`, `manager`, `instructor`,
 * `unaffiliated` — the register's tabs (WS18c). THEY ARE FILTERS OVER FACTS,
 * NOT A DISCRIMINATOR: a graduate who came back to teach is a learner AND an
 * instructor, appears under both, and no column anywhere picks one. `manager`
 * is the one no column can answer at all — it is derived from the arrival that
 * named the buyer, which is the same rule the academy.company role grant reads.
 * Absent or unrecognised means the whole register.
 *
 * `options.q` narrows by a case-insensitive name/email substring — SERVER-SIDE,
 * so a picker works past the first page of the register. Literal % and _ match
 * themselves; the server escapes.
 *
 * Cursor pagination. Feed `nextCursor` back as `page.cursor`; its absence means
 * the last page. `totalCount` is never populated, so a numbered pager cannot be
 * built on this.
 *
 * @param {PageRequest} [page]
 * @param {{
 *   q?: string,
 *   kind?: 'learner' | 'manager' | 'instructor' | 'unaffiliated',
 *   market?: string,
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<import('@academy-api').PagedResult<PersonListItemResponse>>}
 */
export const listPeople = (page, options = {}) => {
    const { q, kind, market, ...rest } = options;
    const params = {
        ...(q ? { q } : {}),
        ...(kind ? { kind } : {}),
        ...(market ? { market } : {}),
    };
    return get('/people', {
        ...rest,
        page,
        ...(Object.keys(params).length > 0 ? { params } : {}),
    });
};

/**
 * The register's status-card counts — narrowed by the header's country
 * filter only, never by the search box or the active tab (those would make
 * the cards restate the table).
 *
 * @param {{ market?: string, signal?: AbortSignal }} [options]
 * @returns {Promise<import('@academy-api').PersonRegisterSummary>}
 */
export const getPeopleSummary = (options = {}) => {
    const { market, ...rest } = options;
    return get('/people/summary', {
        ...rest,
        ...(market ? { params: { market } } : {}),
    });
};

// THERE IS NO createPerson. POST /people still exists server-side — the bare
// row an administrator makes for staff, under academy.trainers.edit — but no
// screen offers it since the trainer flow moved to the HRM: a learner arrives
// from the CRM or the HRM, an instructor from the HRM, and the rare bare
// person is an API act, not a button. The route is annotated
// calledFromClient:false in docs/route-manifest.json.

/**
 * Patch. PASS A DIRTY-FIELD DIFF, NEVER FORM STATE.
 *
 * Absent leaves the column alone; explicit null writes NULL. Handing this the
 * whole form object erases every field the user did not fill in and returns
 * 200. Build the body with buildPatch(original, current) from src/api/patch.js.
 *
 * @param {string} id
 * @param {Partial<PersonPatchRequest>} dirtyFields
 */
export const patchPerson = (id, dirtyFields) => {
    // An empty patch is a round trip that can still fail with stale_write for
    // no gain.
    if (isEmptyPatch(dirtyFields)) return Promise.resolve(null);
    return patch(`/people/${id}`, dirtyFields);
};

/**
 * The same patch applied to many people.
 *
 * `mode` is REQUIRED — there is no default and omitting it is a 400. Here it
 * goes in the QUERY STRING, not the body, because this is a batch PATCH.
 *
 * atomic     -> 200, or one ProblemDetails and NOTHING was written.
 * bestEffort -> 207 with per-item outcomes. A 207 IS NOT A SUCCESS: check
 *               `allSucceeded` before rendering anything green.
 *
 * @param {string[]} ids
 * @param {Partial<PersonPatchRequest>} dirtyFields
 * @param {BatchMode} mode
 */
export const patchManyPeople = (ids, dirtyFields, mode) =>
    patch('/people', dirtyFields, { params: { ids, mode } });

// THERE IS NO attachLearnerProfile AND NO attachInstructorProfile, and both
// absences mirror the server's. The learner route went in WS18c (a learner
// arrives from the CRM or from an enrolment, never from a staff screen); the
// instructor route went when the HRM assignment channel arrived — an
// instructor is an employee the HR system says teaches, and the profile is
// attached by that channel in process, never from here.

/**
 * An NPS atom. Append-only: a correction is a new row, never an edit, and the
 * standing derivation reads the full history.
 *
 * `request.instructorPersonId` must equal `id` or the server returns a
 * validation error — it will not infer one from the other.
 *
 * @param {string} id
 * @param {{ instructorPersonId: string } & Record<string, unknown>} request
 */
export const submitRating = (id, request) => post(`/people/${id}/ratings`, request);

/**
 * A quality audit. A `delisted` outcome also stamps the instructor profile, in
 * the same transaction.
 * @param {string} id
 * @param {{ instructorPersonId: string } & Record<string, unknown>} request
 */
export const recordAudit = (id, request) => post(`/people/${id}/audits`, request);

/**
 * Links a Person to a Keycloak user id (sub) — records who a token subject IS.
 *
 * BEHIND academy.trainers.edit, not the enrollment desk: linking an
 * identity decides who may act as a given person.
 *
 * IT GRANTS NOTHING. Role membership lives in Keycloak client roles and the realm
 * decides what an object id may do; there is deliberately no role-assignment
 * endpoint anywhere in this API. This is also the fix for a caller stuck on
 * 403 `actor_unmatched` — see AcademyApiError.needsProvisioning.
 *
 * @param {string} id
 * @param {LinkIdentityRequest} request
 */
export const linkIdentity = (id, request) => post(`/people/${id}/identity`, request);
